/**
 * The Rettigheter page as the user reads it (docs/qa/rettigheter/UTKAST.md, 30 September).
 *
 * Sections follow the modules an organisation actually uses — the same rule as the menu — and each
 * setting is one choice on screen that may write several actions at once. The texts live in
 * `settings.permissions.section.<id>.*` and `settings.permissions.setting.<id>.*`; no code here is
 * ever shown to the user.
 *
 * A setting groups actions only when they always belong together AND share the default for every
 * role, scope included — `permission-settings.spec.ts` holds that. `venue_registry:delete` is NOT in
 * `venueRegistry` for that reason: its default stops at admin while create/update reach member, so
 * merging it would have changed what a new organisation starts with.
 *
 * `membersInvite` carries `contact:invite` besides `invitation:*`: POST /v1/invitations and
 * POST /v1/contacts/:id/invite guard on `contact:invite` and ask `invitation:create` (with the
 * target role) inside the service, so a switch without it would not move the door (0475).
 */
import type { TemplateAction } from './default-permissions.js';

export type PermissionModule =
  | 'general'
  | 'money'
  | 'festival'
  | 'crew'
  | 'booking'
  | 'artists'
  | 'venue'
  | 'venues'
  | 'concerts'
  | 'sharing';

export interface PermissionSetting {
  readonly id: string;
  readonly actions: readonly TemplateAction[];
  /**
   * SEKSJONER DEN OGSÅ VISES I. Én innstilling, ett sett celler, tegnet to steder: varselet om bud
   * uten ansvarlig gjelder både arrangørens Booking og byråets Artister (F21), og et rent byrå har
   * ikke Booking-seksjonen.
   */
  readonly alsoIn?: readonly PermissionModule[];
}

export interface PermissionSection {
  readonly id: PermissionModule;
  readonly module: PermissionModule;
  readonly settings: readonly PermissionSetting[];
}

export const PERMISSION_SECTIONS: readonly PermissionSection[] = [
  {
    id: 'general',
    module: 'general',
    settings: [
      { id: 'members', actions: ['membership:delete'] },
      {
        id: 'membersInvite',
        actions: ['invitation:create', 'invitation:delete', 'contact:invite'],
      },
      /* F54: se ventende invitasjoner (uten å kunne trekke dem tilbake). */
      { id: 'membersInvitationsSee', actions: ['invitation:list'] },
      { id: 'contactsWrite', actions: ['contact:create', 'contact:update'] },
      { id: 'contactsDelete', actions: ['contact:delete'] },
      { id: 'contactsMerge', actions: ['contact:merge'] },
      { id: 'tasksAssign', actions: ['task:create'] },
      /* 85 (QA13): å gi en oppgave til en annen var en setning i teksten over; nå er det en linje. */
      { id: 'tasksAssignOthers', actions: ['task:assign'] },
      { id: 'tasksComment', actions: ['task:comment'] },
      { id: 'tasksDelete', actions: ['task:delete'] },
      { id: 'threadsStart', actions: ['thread:create'] },
      { id: 'documentsUpload', actions: ['document:create'] },
      { id: 'documentsDelete', actions: ['document:delete'] },
      { id: 'activityLog', actions: ['audit_log:list'] },
      /* docs/EKSPORT.md §6: hvem som kan eksportere (PDF, DOCX, CSV). Standard alle roller. */
      { id: 'export', actions: ['export:create'] },
      /* Sak 239.36: tittel, ansvar og «rapporterer til» på en superadmin. */
      { id: 'superadminProfile', actions: ['superadmin_profile:update'] },
      /* Sak 239.21: «Organisasjoner vi samarbeider med». */
      { id: 'partners', actions: ['organization_partner:create', 'organization_partner:respond'] },
      /* Sak 239.38: hvem som ser og endrer denne siden. Superadmin har dem alltid. */
      { id: 'permissionsRead', actions: ['role_permission:list', 'role_permission:read'] },
      { id: 'permissionsWrite', actions: ['role_permission:update'] },
    ],
  },
  {
    id: 'money',
    module: 'money',
    settings: [
      { id: 'economyRead', actions: ['economy:read'] },
      { id: 'economyWrite', actions: ['economy:create', 'economy:update', 'economy:delete'] },
      { id: 'contractsRead', actions: ['contract:read'] },
      { id: 'contractsWrite', actions: ['contract:update'] },
      { id: 'contractsDraft', actions: ['contract:draft'] },
      { id: 'contractsRegister', actions: ['contract:register'] },
      { id: 'contractsVoid', actions: ['contract:void'] },
    ],
  },
  {
    id: 'festival',
    module: 'festival',
    settings: [
      { id: 'allFestivals', actions: ['festival:access_all'] },
      { id: 'programmeWrite', actions: ['performance:create', 'performance:update'] },
      { id: 'programmeDelete', actions: ['performance:delete'] },
      { id: 'festivalRoles', actions: ['festival:manage_roles'] },
      /* F36: ressurstypene, på festivalen og på spillestedet. */
      {
        id: 'resourceTypesWrite',
        actions: ['resource_type:create', 'resource_type:update', 'resource_type:delete'],
      },
      /* 253.64: delingslinjene står i modulen de deler fra — kjøreplanen og kartet (F13). */
      { id: 'shareRunningOrder', actions: ['stage:share'] },
      { id: 'shareMap', actions: ['map:share'] },
    ],
  },
  /*
   * MANNSKAP OG VAKTER (sak 231.5, Atle 04.10): cellene hører til mannskap og vakter, ikke til
   * festivalmodulen. Seksjonen vises for Festival OG Spillested, så et rent spillested har dem.
   */
  {
    id: 'crew',
    module: 'crew',
    settings: [
      /* F34: masseutsending av beskjed, vaktplan og vaktinfo på e-post og i Innboks. */
      { id: 'broadcastSend', actions: ['broadcast:create', 'broadcast:list'] },
      { id: 'shiftsWrite', actions: ['shift:create', 'shift:update', 'shift:delete'] },
      /* QA16 297.4: melde seg på ledige vakter og bytte, og hvem som godkjenner det. */
      { id: 'shiftsRequest', actions: ['shift:request'] },
      { id: 'shiftsApprove', actions: ['shift:assign'] },
      { id: 'accreditationWrite', actions: ['accreditation:create', 'accreditation:update'] },
    ],
  },
  /*
   * BOOKING, ELLEVE LINJER (G2, QA13 #87, 0923). Til 0923 styrte Festivals «Lage og endre
   * programmet» hele registeret. Startverdiene er kopiert fra de gamle cellene, så ingenting endret
   * seg før noen flyttet en bryter. Å flytte et godtatt bud inn i programmet krever både «Gi, sende
   * og trekke bud» her og «Lage og endre programmet» under Festival — to brytere, ingen kryssing.
   */
  {
    id: 'booking',
    module: 'booking',
    settings: [
      { id: 'bookingSee', actions: ['booking:list'] },
      { id: 'bookingArtistsWrite', actions: ['booking_artist:create', 'booking_artist:update'] },
      /* 253.62: «Programmere» er en egen linje. */
      { id: 'bookingProgramme', actions: ['booking_draft:create', 'booking_draft:update'] },
      { id: 'bookingEnquiriesSend', actions: ['booking_enquiry:create'] },
      { id: 'bookingBids', actions: ['booking_bid:create', 'booking_bid:update'] },
      // F22: «Svare på motbud» — godta byråets motbud eller gi et nytt bud.
      { id: 'bidRespond', actions: ['booking_bid:respond'] },
      /* 253.63: standard Medarbeider, som i dag. */
      { id: 'bookingAmounts', actions: ['booking_amount:read'] },
      { id: 'bookingDelete', actions: ['booking:delete'] },
      // Sak 237.2: «Slette artister i booking» — slette egne og skjule skannede.
      { id: 'bookingArtistsDelete', actions: ['booking_artist:delete'] },
      { id: 'agencyMoves', actions: ['agency_move:list'] },
      /*
       * F21: varsel om bud og forespørsler ingen agent er ansvarlig for. Flyttet fra Generelt (87).
       * Den gjelder også byrået (nye bud), så den tegnes i Artister også — én innstilling.
       */
      { id: 'bookingUnassignedNotice', actions: ['booking_enquiry:notify'], alsoIn: ['artists'] },
    ],
  },
  /* ARTISTER, byråets stall (G2, 0923). «Avtaler og provisjon» er justerbar fra 0923 (253.61). */
  {
    id: 'artists',
    module: 'artists',
    settings: [
      { id: 'artistsSee', actions: ['artist:list'] },
      { id: 'artistsWrite', actions: ['artist:update'] },
      { id: 'agencyBookingsRespond', actions: ['agency_booking:respond'] },
      { id: 'agencyBookingsWrite', actions: ['agency_booking:create', 'agency_booking:update'] },
      { id: 'agreementsRead', actions: ['artist_agreement:read'] },
      { id: 'agreementsWrite', actions: ['artist_agreement:update'] },
      { id: 'artistsInvite', actions: ['artist:invite'] },
      { id: 'artistsArchive', actions: ['artist:archive'] },
      /* 253.64: å dele en delt advance videre hører til modulen den deles fra. */
      { id: 'sharedAdvances', actions: ['shared_advance:read'], alsoIn: ['booking', 'concerts'] },
      {
        id: 'sharedAdvancesShare',
        actions: ['shared_advance:share'],
        alsoIn: ['booking', 'concerts'],
      },
    ],
  },
  {
    id: 'venue',
    module: 'venue',
    settings: [
      { id: 'rooms', actions: ['venue:create', 'venue:update'] },
      { id: 'eventsWrite', actions: ['venue_night:create', 'venue_night:update'] },
      /* F49: slette arrangementer, også «denne og senere» og «alle» i en serie. */
      { id: 'eventsDelete', actions: ['venue_night:delete'] },
      { id: 'roomRates', actions: ['venue_rate:read'] },
      /* QA16 301.10: hvem som endrer leien, og hvem som ser og bruker kontrollene for synlighet og kalender. */
      { id: 'roomRatesWrite', actions: ['venue_rate:update'] },
      { id: 'roomPublish', actions: ['venue:publish'] },
      { id: 'dateRequestsAnswer', actions: ['venue_enquiry:respond'] },
      /* F18: dele salens spesifikasjon og arrangementet med en ekstern. */
      { id: 'roomsShare', actions: ['venue:share'] },
      { id: 'eventsShare', actions: ['venue_night:share'] },
    ],
  },
  {
    id: 'venues',
    module: 'venues',
    settings: [
      { id: 'venueRegistry', actions: ['venue_registry:create', 'venue_registry:update'] },
      { id: 'dateRequestsSend', actions: ['date_request:create'] },
    ],
  },
  {
    id: 'concerts',
    module: 'concerts',
    settings: [
      { id: 'concertsCreate', actions: ['concert:create'] },
      { id: 'concertsWrite', actions: ['concert:update'] },
      { id: 'concertBudgetRead', actions: ['concert_budget:read'] },
      { id: 'concertBudgetEdit', actions: ['concert_budget:update'] },
      { id: 'concertTemplates', actions: ['concert_template:create', 'concert_template:update'] },
    ],
  },
  {
    id: 'sharing',
    module: 'sharing',
    settings: [
      { id: 'externalInvite', actions: ['external:invite'] },
      { id: 'shareLinks', actions: ['share_link:create'] },
      { id: 'shareResourcesShifts', actions: ['resource:share', 'shift:share'] },
      { id: 'shareDepartments', actions: ['department:share'] },
      { id: 'shareEditLevel', actions: ['share:edit_level'] },
      /* F12: dokumenter og mapper. Kjøreplanen og kartet står under Festival (253.64). */
      { id: 'shareDocuments', actions: ['document:share'] },
      { id: 'shareFolders', actions: ['folder:share'] },
      /* F9: oversikten «Delinger». */
      { id: 'sharesOverview', actions: ['share:list'] },
    ],
  },
];
